import { useState } from "react";
import InteractiveStage from "./InteractiveStage";

const SYMBOLS = ["A", "7", "◆", "$", "♣", "★"];
const DOTS = Array.from({ length: 24 });

const SlotMachine = ({ active, values }) => (
  <div className={`mini-game slot-game ${active ? "is-active" : ""}`}>
    <div className="slot-game__top">ATHERIAM ARCADE</div>
    <div className="slot-game__window">
      {values.map((value, index) => (
        <span key={index}>{SYMBOLS[value]}</span>
      ))}
    </div>
    <div className="slot-game__controls">
      <i />
      <b>PLAY</b>
      <em>ATH</em>
    </div>
    <div className="slot-game__base">PLAYER ONE</div>
  </div>
);

const SecurityScanner = ({ active }) => (
  <div className={`mini-game security-game ${active ? "is-active" : ""}`}>
    <div className="security-game__halo" />
    <div className="security-game__shield">
      <img src="/favicon.ico" alt="" className="security-game__logo" />
      <i />
    </div>
    <div className="security-game__scanner" />
    <div className="security-game__nodes">
      {[0, 1, 2, 3, 4].map((node) => <i key={node} />)}
    </div>
    <div className="game-label">
      <span>{active ? "THREAT BLOCKED" : "CHAIN GUARD"}</span>
      <b>{active ? "100%" : "READY"}</b>
    </div>
  </div>
);

const PachinkoProof = ({ active }) => (
  <div className={`mini-game proof-game ${active ? "is-active" : ""}`}>
    <div className="proof-game__header">PROVABLY FAIR</div>
    <div className="proof-game__board">
      {DOTS.map((_, index) => <i key={index} />)}
      <span className="proof-game__ball" />
      <div className="proof-game__bins">
        <b>0</b><b>1</b><b>0</b><b>1</b><b>1</b>
      </div>
    </div>
    <div className="game-label">
      <span>{active ? "HASH MATCHED" : "DROP TO VERIFY"}</span>
      <b>0xATH</b>
    </div>
  </div>
);

const VaultCracker = ({ active }) => (
  <div className={`mini-game vault-game ${active ? "is-active" : ""}`}>
    <div className="vault-game__case">
      <div className="vault-game__door">
        <span className="vault-game__dial">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <i key={i} />)}
          <img src="/favicon.ico" alt="" className="vault-game__logo" />
        </span>
      </div>
      <div className="vault-game__inside">
        <span>ATH</span><span>ATH</span><span>ATH</span>
      </div>
    </div>
    <div className="game-label">
      <span>{active ? "ASSETS UNLOCKED" : "YOUR KEYS"}</span>
      <b>{active ? "+750" : "LOCKED"}</b>
    </div>
  </div>
);

const RocketLauncher = ({ active }) => (
  <div className={`mini-game launch-game ${active ? "is-active" : ""}`}>
    <div className="launch-game__sky">
      {Array.from({ length: 10 }).map((_, i) => <i key={i} />)}
    </div>
    <div className="launch-game__rocket">
      <span />
      <b>ATH</b>
      <div><i /><i /><i /></div>
    </div>
    <div className="launch-game__pad" />
    <div className="game-label">
      <span>{active ? "PAYOUT SENT" : "ATH EXPRESS"}</span>
      <b>{active ? "0.8s" : "READY"}</b>
    </div>
  </div>
);

const CapsuleDrop = ({ active }) => (
  <div className={`mini-game capsule-game ${active ? "is-active" : ""}`}>
    <div className="capsule-game__dome">
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <i key={i} style={{ "--i": i }}><b>{i % 2 ? "★" : "A"}</b></i>
      ))}
    </div>
    <div className="capsule-game__chute">
      <span><b>ATH</b></span>
    </div>
    <div className="capsule-game__knob" />
    <div className="game-label">
      <span>{active ? "RARE DROP" : "REWARD CAPSULE"}</span>
      <b>{active ? "+250" : "CLAIM"}</b>
    </div>
  </div>
);

const NetworkCore = ({ active }) => (
  <div className={`mini-game core-game ${active ? "is-active" : ""}`}>
    <div className="core-game__orbit core-game__orbit--outer" />
    <div className="core-game__orbit core-game__orbit--inner" />
    <div className="core-game__links">
      {[0, 1, 2, 3, 4].map((index) => <i key={index} />)}
    </div>
    <div className="core-game__center">
      <img src="/favicon.ico" alt="Atheriam" />
    </div>
    {[
      ["SEC", "Security"],
      ["FAST", "Speed"],
      ["FAIR", "Fair Play"],
      ["P2P", "Direct"],
      ["OWN", "Assets"],
    ].map(([code, label], index) => (
      <div
        key={code}
        className={`core-game__node core-game__node--${index}`}
        style={{ "--i": index }}
      >
        <b>{code}</b>
        <small>{label}</small>
      </div>
    ))}
    <div className="game-label">
      <span>{active ? "ALL FEATURES SYNCED" : "ATHERIAM NETWORK"}</span>
      <b>{active ? "LIVE" : "5 NODES"}</b>
    </div>
  </div>
);

const CoinPusher = ({ active }) => (
  <div className={`mini-game pusher-game ${active ? "is-active" : ""}`}>
    <div className="pusher-game__sign">$ATH JACKPOT</div>
    <div className="pusher-game__glass">
      <div className="pusher-game__shelf">
        {Array.from({ length: 12 }).map((_, i) => <i key={i}>A</i>)}
      </div>
      <span className="pusher-game__drop">ATH</span>
    </div>
    <div className="pusher-game__tray">
      <span>{active ? "TOKEN UNLOCKED" : "INSERT ATH"}</span>
    </div>
  </div>
);

const CONCEPTS = {
  security: { hint: "Tap to scan", accent: "blue", Component: SecurityScanner },
  fair: { hint: "Tap to drop proof", accent: "brand", Component: PachinkoProof },
  vault: { hint: "Tap to crack vault", accent: "purple", Component: VaultCracker },
  gaming: { hint: "Tap to spin", accent: "pink", Component: SlotMachine },
  payout: { hint: "Tap to launch payout", accent: "purple", Component: RocketLauncher },
  rewards: { hint: "Tap for reward drop", accent: "brand", Component: CapsuleDrop },
  core: { hint: "Tap to sync features", accent: "blue", Component: NetworkCore },
  token: { hint: "Tap to push $ATH", accent: "brand", Component: CoinPusher },
};

const ArcadeMachine = ({ mode = "gaming" }) => {
  const concept = CONCEPTS[mode] || CONCEPTS.gaming;
  const Scene = concept.Component;
  const [active, setActive] = useState(false);
  const [values, setValues] = useState([0, 1, 4]);

  const play = () => {
    setActive(false);
    window.requestAnimationFrame(() => setActive(true));

    if (mode === "gaming") {
      let ticks = 0;
      const timer = window.setInterval(() => {
        setValues([
          Math.floor(Math.random() * SYMBOLS.length),
          Math.floor(Math.random() * SYMBOLS.length),
          Math.floor(Math.random() * SYMBOLS.length),
        ]);
        ticks += 1;
        if (ticks > 11) {
          window.clearInterval(timer);
          setValues([0, 0, 0]);
        }
      }, 70);
    }

    window.setTimeout(() => setActive(false), 1700);
  };

  return (
    <InteractiveStage
      className={`concept-scene concept-scene--${concept.accent} concept-scene--${mode}`}
      hint={concept.hint}
      onActivate={play}
    >
      <Scene active={active} values={values} />
    </InteractiveStage>
  );
};

export default ArcadeMachine;
